import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Package, Plus, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { itemApi } from '../../api/endpoints';
import { itemsQuery } from '../../api/queries';
import type { Item } from '../../api/types';
import { useAccess } from '../../app/access';
import { useI18n } from '../../i18n/I18nProvider';
import { useDebouncedValue } from '../../lib/hooks';
import { Button } from '../../ui/Button';
import { Money } from '../../ui/Layout';
import { Sheet } from '../../ui/Sheet';
import styles from '../sales/Sales.module.css';
import { ItemForm } from './ItemForm';

/**
 * T.01–T.02, «الاختيار من الكتالوج» (BLOCK-1-REDESIGN §A.4): a side sheet at the inline end, so the
 * invoice lines stay visible beside it. Search the catalogue (250 ms, 50 results), tick several
 * items, add them as lines. «صنف جديد في الكتالوج» switches the same sheet to the item form, with a
 * way back; a created item returns to the list already ticked.
 */
export function ItemPickerSheet({
  orgId,
  isOpen,
  onOpenChange,
  onConfirm,
}: {
  orgId: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (items: Item[]) => void;
}) {
  const { t } = useI18n();
  const access = useAccess(orgId);
  const [q, setQ] = useState('');
  const debounced = useDebouncedValue(q, 250);
  const [selected, setSelected] = useState<Map<string, Item>>(new Map());
  const [view, setView] = useState<'list' | 'new'>('list');
  useEffect(() => {
    if (isOpen) {
      setQ('');
      setSelected(new Map());
      setView('list');
    }
  }, [isOpen]);
  const items = useQuery({ ...itemsQuery(orgId, { q: debounced, limit: 50 }), enabled: isOpen });

  const toggle = (item: Item) =>
    setSelected((m) => {
      const next = new Map(m);
      if (next.has(item.id)) next.delete(item.id);
      else next.set(item.id, item);
      return next;
    });

  return (
    <Sheet
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title={view === 'new' ? t.catalogue.newTitle : t.catalogue.pickerTitle}
      testId="item-picker"
      headerStart={
        view === 'new' ? (
          <Button
            variant="ghost"
            size="sm"
            icon={<ArrowLeft size={16} className="flip-rtl" aria-hidden="true" />}
            onPress={() => setView('list')}
            className={styles.textButton}
          >
            {t.catalogue.backToList}
          </Button>
        ) : undefined
      }
      footer={
        view === 'list' ? (
          <div className={styles.sheetFooter}>
            {access.can('item.create') ? (
              <Button
                variant="ghost"
                icon={<Plus size={16} aria-hidden="true" />}
                onPress={() => setView('new')}
                testId="picker-new-item"
              >
                {t.catalogue.newItem}
              </Button>
            ) : (
              <span />
            )}
            <span className={styles.actionStatus} aria-live="polite">
              {t.catalogue.selected(selected.size)}
            </span>
            <Button
              variant="primary"
              isDisabled={selected.size === 0}
              testId="item-picker-confirm"
              onPress={() => {
                onConfirm([...selected.values()]);
                onOpenChange(false);
              }}
            >
              {t.catalogue.addSelected(selected.size)}
            </Button>
          </div>
        ) : undefined
      }
    >
      {view === 'new' ? (
        <ItemForm
          orgId={orgId}
          itemId={null}
          onCancel={() => setView('list')}
          onSaved={({ id }) => {
            // The new item comes back ticked (and listed after the refetch).
            void itemApi.get(orgId, id).then((d) => {
              const item: Item = { ...d, defaultTaxIds: d.defaultTaxes.map((x) => x.id) };
              setSelected((m) => new Map(m).set(item.id, item));
              void items.refetch();
            });
            setView('list');
          }}
        />
      ) : (
        <div className={styles.formStack}>
          <label className={styles.search}>
            <Search size={16} aria-hidden="true" />
            <span className="visually-hidden">{t.catalogue.pickerSearch}</span>
            <input
              type="search"
              value={q}
              autoFocus
              placeholder={t.catalogue.pickerSearch}
              onChange={(e) => setQ(e.target.value)}
              data-testid="item-picker-search"
            />
          </label>
          <ul className={styles.pickList} aria-label={t.catalogue.pickerTitle}>
            {(items.data?.items ?? []).map((item) => {
              const on = selected.has(item.id);
              return (
                <li key={item.id}>
                  <label
                    className={styles.pickRow}
                    data-selected={on}
                    data-testid={`pick-${item.internalCode}`}
                  >
                    <input type="checkbox" checked={on} onChange={() => toggle(item)} />
                    <span>
                      <span className={styles.pickTitle}>{item.nameAr}</span>
                      <span className={`${styles.subtle} ${styles.mono} ${styles.pickMeta}`}>
                        {item.internalCode} · {item.etaItemType} {item.etaItemCode} ·{' '}
                        {item.unitCode}
                      </span>
                    </span>
                    <Money value={item.unitPrice} currency="EGP" />
                  </label>
                </li>
              );
            })}
            {items.data && items.data.items.length === 0 ? (
              <li className={styles.emptyLines}>
                <Package size={22} aria-hidden="true" />
                {t.catalogue.empty}
              </li>
            ) : null}
          </ul>
        </div>
      )}
    </Sheet>
  );
}
